import { proxyCustomElement, HTMLElement, createEvent, h, Host, Fragment } from '@stencil/core/internal/client';
import { d as debounce } from './debounce.js';
import { t as toLabel, a as toValue } from './index5.js';
import { g as getFilterOptionsFn, h as handleOptions, i as isOtherOption } from './index6.js';
import { d as defineCustomElement$c, H as HiddenHTMLDataList } from './input.js';
import { d as defineCustomElement$i } from './box.js';
import { d as defineCustomElement$h } from './check-input.js';
import { d as defineCustomElement$g } from './close-button.js';
import { d as defineCustomElement$f } from './divider.js';
import { d as defineCustomElement$e } from './form-field.js';
import { d as defineCustomElement$d } from './icon.js';
import { d as defineCustomElement$b } from './list.js';
import { d as defineCustomElement$a } from './list-item.js';
import { d as defineCustomElement$9 } from './list-item-divider.js';
import { d as defineCustomElement$8 } from './list-item-header.js';
import { d as defineCustomElement$7 } from './list-item-meta.js';
import { d as defineCustomElement$6 } from './loader.js';
import { d as defineCustomElement$5 } from './menu-pane.js';
import { d as defineCustomElement$4 } from './status.js';
import { d as defineCustomElement$3 } from './tooltip.js';
import { d as defineCustomElement$2 } from './tooltip-label.js';

const autocompleteCss = ":host{-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;box-sizing:border-box;font-family:Open Sans;font-size:.875rem;font-weight:400;letter-spacing:-.0004em;line-height:1.25rem;line-height:normal;position:relative;width:100%}:host *,:host :after,:host :before{box-sizing:inherit}:host img{flex:none;overflow:hidden;text-overflow:ellipsis;white-space:pre-line}";
const WuiAutocompleteStyle0 = autocompleteCss;

const WuiAutocomplete$1 = /*@__PURE__*/ proxyCustomElement(class WuiAutocomplete extends HTMLElement {
    constructor() {
        super();
        this.__registerHost();
        this.__attachShadow();
        this.choose = createEvent(this, "choose", 3);
        this.searchable = true;
        this.abortControllers = [];
        this.filterOptions = getFilterOptionsFn(this);
        this.filterOptionsDebounced = debounce(this.filterOptions, 250);
        this.value = undefined;
        this.name = undefined;
        this.options = undefined;
        this.placeholder = '';
        this.disabled = false;
        this.loading = false;
        this.canClear = true;
        this.error = undefined;
        this.noResultsMessage = 'No results';
        this.suggestionsAction = undefined;
        this.open = false;
        this.searchQuery = '';
        this.optionsResolved = undefined;
    }
    async watchOptions(newValue) {
        return handleOptions(this, newValue);
    }
    focus() {
        this.wuiInputElm?.focus();
    }
    async handleKeyDown(e) {
        // Handle keyboard navigating through MenuPane
        const wuiMenuPaneElement = this.el.shadowRoot?.querySelector('wui-menu-pane');
        if (wuiMenuPaneElement && (await wuiMenuPaneElement.handleKeyDown(e))) {
            // Event was handled by menu pane
            this.open = !!wuiMenuPaneElement.open;
            return;
        }
        const wuiListElement = this.el.shadowRoot?.querySelector('wui-list');
        if (wuiListElement && (await wuiListElement.handleKeyDown(e))) {
            return;
        }
        // If the list did not handle the 'Enter' key, then it means there
        // was no option to select. In that case we'll emit the `choose`
        // event with the current value as a non-existing option.
        if (e.key === 'Enter') {
            // Emit the input's current value
            this.chooseValue(this.value, false);
        }
    }
    componentWillLoad() {
        const old = {
            loading: this.loading,
            disabled: this.disabled,
        };
        this.loading = true;
        this.disabled = true;
        // Trigger options validation for initial value
        this.watchOptions(this.options).then(() => {
            this.loading = old.loading;
            this.disabled = old.disabled;
        });
    }
    toggleMenuPane(open = !this.open) {
        if (this.disabled || this.open === open) {
            return;
        }
        this.open = open;
    }
    setValue(newValue) {
        this.value = newValue;
        this.searchQuery = newValue; // Equal to value
        this.el.dispatchEvent(new window.Event('input', { bubbles: true }));
    }
    chooseValue(value, exists) {
        // Emit the select event with the chosen option's value
        const event = this.choose.emit({ value, exists });
        if (!event.defaultPrevented) {
            // Clear input
            this.setValue('');
            // Close menu pane
            this.toggleMenuPane(false);
        }
    }
    handleSelect(option) {
        const newValue = toValue(option ?? '');
        this.chooseValue(newValue, true);
    }
    handleQuery(e) {
        const { value } = e.target;
        // Prevent inner 'input' event from firing
        e.stopPropagation();
        e.preventDefault();
        this.setValue(value);
        this.filterOptionsDebounced();
        // Toggle menu based on value
        this.toggleMenuPane(!!this.value);
    }
    render() {
        return (h(Host, { key: '16fd47e41fa20b67797ee17f4fec99bf233df8ea', tabIndex: 0, open: this.open }, h(HiddenHTMLDataList, { key: '9c3db38af0f389b1dafee9daf2f11d7b63619092', id: this.name, options: this.optionsResolved, value: this.value }), h("wui-input", { key: '2bb8acb3f64f341a775728568704549bf4900ae5', type: "search", list: this.name, value: this.value, placeholder: this.placeholder, loading: this.loading, name: this.name, disabled: this.disabled, error: this.error, canClear: this.canClear, ref: (elm) => {
                this.wuiInputElm = elm;
            }, onInput: (e) => {
                this.handleQuery(e);
            }, onKeyDown: (e) => {
                this.handleKeyDown(e);
            } }), h("wui-menu-pane", { key: '8a3ef34751a76f9a285997faff13253b3b44b7ed', open: this.open, stretch: "min", placement: "bottom", onClickOutside: () => {
                this.toggleMenuPane(false);
            }, onOpenChanged: (e) => {
                this.toggleMenuPane(e.detail);
            } }, h("wui-list", { key: 'd23e58d765f0f461612a4dc8cfcc0cc24ba29d2d', scrollSize: "md" }, this.optionsResolved?.length ? (this.optionsResolved.map((option, idx) => {
            if (isOtherOption(option)) {
                switch (option.type) {
                    case 'header':
                        return h("wui-list-item-header", { key: idx, ...option });
                    case 'divider':
                        return h("wui-list-item-divider", { key: idx });
                    case 'meta':
                        return h("wui-list-item-meta", { key: idx, ...option });
                }
            }
            return (h("wui-list-item", {
                // ❓ We shouldn't use an array index for unique key, but
                // for some reason, if we use the unique value of `option`,
                // the whole rendering gets messed up during list keyboard
                // navigation. No idea what's going on there...
                key: idx, label: `${toLabel(option)}`, disabled: !this.open || (typeof option === 'object' ? option.disabled : false), onClick: () => this.handleSelect(option), description: typeof option === 'object' ? option.description : undefined
            }));
        })) : (h("wui-list-item-meta", { label: this.noResultsMessage })), (() => {
            if (!this.suggestionsAction) {
                return;
            }
            if (!this.value) {
                return;
            }
            const suggestionMatchesValue = this.optionsResolved?.some((s) => !isOtherOption(s) && toValue(s) === this.value);
            if (suggestionMatchesValue) {
                return;
            }
            return (h(Fragment, null, h("wui-list-item-divider", null), h("wui-list-item", { variant: "button", label: [
                    this.suggestionsAction.action,
                    ' "',
                    [this.value, 'bold'],
                    '"',
                    this.suggestionsAction.type ? ` ${this.suggestionsAction.type}` : '',
                ], icon: this.suggestionsAction.icon ?? 'plus', onClick: () => {
                    this.toggleMenuPane(false);
                    if (!this.value) {
                        return;
                    }
                    this.suggestionsAction?.onSelect(this.value);
                } })));
        })()))));
    }
    get el() { return this; }
    static get watchers() { return {
        "options": ["watchOptions"]
    }; }
    static get style() { return WuiAutocompleteStyle0; }
}, [1, "wui-autocomplete", {
        "value": [1025],
        "name": [1],
        "options": [16],
        "placeholder": [1],
        "disabled": [1540],
        "loading": [1540],
        "canClear": [4, "can-clear"],
        "error": [520],
        "noResultsMessage": [1, "no-results-message"],
        "suggestionsAction": [16],
        "open": [32],
        "searchQuery": [32],
        "optionsResolved": [32]
    }, undefined, {
        "options": ["watchOptions"]
    }]);
function defineCustomElement$1() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["wui-autocomplete", "wui-box", "wui-check-input", "wui-close-button", "wui-divider", "wui-form-field", "wui-icon", "wui-input", "wui-list", "wui-list-item", "wui-list-item-divider", "wui-list-item-header", "wui-list-item-meta", "wui-loader", "wui-menu-pane", "wui-status", "wui-tooltip", "wui-tooltip-label"];
    components.forEach(tagName => { switch (tagName) {
        case "wui-autocomplete":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, WuiAutocomplete$1);
            }
            break;
        case "wui-box":
            if (!customElements.get(tagName)) {
                defineCustomElement$i();
            }
            break;
        case "wui-check-input":
            if (!customElements.get(tagName)) {
                defineCustomElement$h();
            }
            break;
        case "wui-close-button":
            if (!customElements.get(tagName)) {
                defineCustomElement$g();
            }
            break;
        case "wui-divider":
            if (!customElements.get(tagName)) {
                defineCustomElement$f();
            }
            break;
        case "wui-form-field":
            if (!customElements.get(tagName)) {
                defineCustomElement$e();
            }
            break;
        case "wui-icon":
            if (!customElements.get(tagName)) {
                defineCustomElement$d();
            }
            break;
        case "wui-input":
            if (!customElements.get(tagName)) {
                defineCustomElement$c();
            }
            break;
        case "wui-list":
            if (!customElements.get(tagName)) {
                defineCustomElement$b();
            }
            break;
        case "wui-list-item":
            if (!customElements.get(tagName)) {
                defineCustomElement$a();
            }
            break;
        case "wui-list-item-divider":
            if (!customElements.get(tagName)) {
                defineCustomElement$9();
            }
            break;
        case "wui-list-item-header":
            if (!customElements.get(tagName)) {
                defineCustomElement$8();
            }
            break;
        case "wui-list-item-meta":
            if (!customElements.get(tagName)) {
                defineCustomElement$7();
            }
            break;
        case "wui-loader":
            if (!customElements.get(tagName)) {
                defineCustomElement$6();
            }
            break;
        case "wui-menu-pane":
            if (!customElements.get(tagName)) {
                defineCustomElement$5();
            }
            break;
        case "wui-status":
            if (!customElements.get(tagName)) {
                defineCustomElement$4();
            }
            break;
        case "wui-tooltip":
            if (!customElements.get(tagName)) {
                defineCustomElement$3();
            }
            break;
        case "wui-tooltip-label":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
    } });
}
defineCustomElement$1();

const WuiAutocomplete = WuiAutocomplete$1;
const defineCustomElement = defineCustomElement$1;

export { WuiAutocomplete, defineCustomElement };

//# sourceMappingURL=wui-autocomplete.js.map